<script lang="ts" setup>
import { QuillEditor, Quill, Delta } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import { storeToRefs } from "pinia";
import { RevisionAnnotation } from "types/RevisionAnnotation";
import { useAnnotationTokenHighlight } from "~/stores/useAnnotationTokenHighlight";

const { annotation } = defineProps({
  annotation: {
    type: Object as PropType<RevisionAnnotation>,
    required: true,
  },
});

const annotationTokenHighlightStore = useAnnotationTokenHighlight();
const { activeAnnotation } = storeToRefs(annotationTokenHighlightStore);

const top = ref<number | undefined>(undefined);
const left = ref<number | undefined>(undefined);

const containerRef = ref<HTMLElement | undefined>(undefined);

const hide = () => {
  top.value = undefined;
  left.value = undefined;
};

watch(activeAnnotation, () => {
  hide();
  if (!activeAnnotation.value) return;

  if (activeAnnotation.value.id != annotation.id) return;

  var annotationEl = document.getElementById(annotation.id!);

  if (!annotationEl) return;

  var annotationRect = annotationEl.getBoundingClientRect();

  if (!annotationRect) return;

  var containerElement = containerRef.value;

  if (!containerElement) return;

  var containerRect = containerElement.getBoundingClientRect();

  if (!containerRect) return;

  const annotationCenter = annotationRect.height / 2 + annotationRect.top;

  const documentRect = useDocumentRect();
  top.value = annotationCenter - containerRect.height / 2;
  left.value = documentRect.left + documentRect.width;
});

const isAnnotationVisible = computed(
  () => activeAnnotation.value?.id == annotation.id
);
</script>

<template>
  <Transition>
    <div
      id="annotation-tooltip-container"
      ref="containerRef"
      class="fixed w-[360px] rounded shadow bg-surface p-4 z-30 transition-all"
      :class="{
        'opacity-0 translate-x-[2000px]': isAnnotationVisible != true,
        '-translate-x-[50px]': isAnnotationVisible == true,
      }"
      :style="{
        top: `${top}px`,
        left: `${left}px`,
      }"
    >
      <BaseDocumentTextEditor
        :key="annotation?.commentObject"
        :model-value="annotation?.commentObject"
        :read-only="true"
      />
    </div>
  </Transition>
</template>

<style>
#annotation-tooltip-container .ql-toolbar {
  display: none !important;
}

.v-enter-active,
.v-leave-active {
  transition: all 0.25s ease-in-out;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
  transform: translateX(200%);
}
</style>
